import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Plus } from "lucide-react"
import { useState } from "react"
import { Link, useNavigate } from "react-router"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { Boton } from "@/componentes/ui/Boton"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { tienePermiso } from "@/lib/autorizacion"
import { comoFecha } from "@/lib/fechas"
import { usarSesion } from "@/lib/sesion"

import { ESTADO, type Evaluacion, type EvaluacionResumen, nota } from "./tipos"

/** La nota y su nivel van en columnas aparte: en pantalla comparten celda. */
function documentoDeEvaluaciones(lista: EvaluacionResumen[]): DocumentoExportable {
  return {
    titulo: "Evaluaciones del desempeño",
    subtitulo: `${lista.length} evaluaciones`,
    secciones: [
      {
        titulo: "Evaluaciones del desempeño",
        columnas: [
          { titulo: "Persona", tipo: "texto" },
          { titulo: "Formulario", tipo: "texto" },
          { titulo: "Desde", tipo: "fecha" },
          { titulo: "Hasta", tipo: "fecha" },
          { titulo: "Estado", tipo: "texto" },
          { titulo: "Autoevaluación", tipo: "texto" },
          { titulo: "Nota final", tipo: "monto" },
          { titulo: "Nivel", tipo: "texto" },
        ],
        filas: lista.map((e) => ({
          celdas: [
            e.persona,
            e.formulario,
            e.desde.slice(0, 10),
            e.hasta.slice(0, 10),
            ESTADO[e.estado],
            e.auto_enviada_en ? "Entregada" : "Sin entregar",
            e.final === null ? null : Number(e.final),
            e.nivel,
          ],
        })),
      },
    ],
  }
}

/**
 * Talento Humano → Evaluaciones (28/10): el programa de evaluación del desempeño de la
 * Dirección, en el sistema. Cada evaluación se abre por persona y período (trimestral) con
 * el formulario de su cargo; el trabajador hace su autoevaluación en «Mis evaluaciones».
 */
export function ListaEvaluaciones() {
  const usuario = usarSesion((e) => e.usuario)
  const puede = tienePermiso(usuario, "evaluacion:administrar")
  const navegar = useNavigate()
  const clienteQuery = useQueryClient()
  const [nueva, setNueva] = useState(false)
  const [persona, setPersona] = useState("")
  const [desde, setDesde] = useState("")
  const [hasta, setHasta] = useState("")

  const lista = useQuery({
    queryKey: ["evaluaciones"],
    queryFn: () => api.get<EvaluacionResumen[]>("/api/v1/evaluaciones"),
  })
  const personas = useQuery({
    queryKey: ["personas", "empleados"],
    queryFn: () =>
      api.get<{ items: { id: number; nombres: string; apellidos: string; tipo: string }[] }>(
        "/api/v1/personas?estado=ACTIVO&limit=200",
      ),
    enabled: nueva,
  })
  const crear = useMutation({
    mutationFn: () =>
      api.post<Evaluacion>("/api/v1/evaluaciones", {
        persona_id: Number(persona),
        desde,
        hasta,
      }),
    onSuccess: (ev) => {
      clienteQuery.invalidateQueries({ queryKey: ["evaluaciones"] })
      navegar(`/talento-humano/evaluacion/${ev.id}`)
    },
  })

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Trimestral y participativa: el trabajador se autoevalúa, la Dirección califica por
          separado y en la sesión 1 a 1 se acuerdan las notas y el PDI.
        </p>
        <div className="flex flex-wrap items-start gap-2">
          {!!lista.data?.length && (
            <Exportar
              titulo="Evaluaciones del desempeño"
              documento={() => documentoDeEvaluaciones(lista.data ?? [])}
            />
          )}
          {puede && !nueva && (
            <Boton variante="primario" onClick={() => setNueva(true)}>
              <Plus size={16} /> Nueva evaluación
            </Boton>
          )}
        </div>
      </div>

      {nueva && (
        <form
          className="tarjeta grid gap-3 p-4 sm:grid-cols-4 sm:items-end"
          onSubmit={(e) => {
            e.preventDefault()
            crear.mutate()
          }}
        >
          <label className="text-sm sm:col-span-2">
            Persona
            <select
              className="campo mt-1"
              required
              value={persona}
              onChange={(e) => setPersona(e.target.value)}
            >
              <option value="">Elige…</option>
              {(personas.data?.items ?? [])
                .filter((p) => p.tipo !== "CLIENTE")
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nombres} {p.apellidos}
                  </option>
                ))}
            </select>
          </label>
          <label className="text-sm">
            Desde
            <input
              type="date"
              required
              className="campo mt-1"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </label>
          <label className="text-sm">
            Hasta
            <input
              type="date"
              required
              className="campo mt-1"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
            />
          </label>
          <p className="text-xs sm:col-span-3" style={{ color: "var(--fg-muted)" }}>
            El formulario sale del cargo: tramitador, ejecutivo de cuentas o, en Finanzas, con el
            bloque de gestión financiera.
          </p>
          <div className="flex gap-2">
            <Boton type="submit" variante="primario" disabled={crear.isPending}>
              Abrir
            </Boton>
            <Boton onClick={() => setNueva(false)}>Cancelar</Boton>
          </div>
          {crear.isError && (
            <p
              role="alert"
              className="text-sm sm:col-span-4"
              style={{ color: "var(--destructive)" }}
            >
              {(crear.error as Error).message}
            </p>
          )}
        </form>
      )}

      {lista.isPending && <EstadoCarga etiqueta="Cargando las evaluaciones" />}
      {lista.isError && (
        <EstadoError
          titulo="No se pudieron cargar"
          error={lista.error}
          onReintentar={() => lista.refetch()}
        />
      )}
      {lista.data && (
        <Tabla etiqueta="Evaluaciones del desempeño">
          <Cabecera>
            <Th>Persona</Th>
            <Th>Período</Th>
            <Th>Estado</Th>
            <Th className="text-right">Nota</Th>
            <Th>
              <span className="sr-only">Abrir</span>
            </Th>
          </Cabecera>
          <Cuerpo>
            {lista.data.length === 0 && (
              <TablaVacia colSpan={5}>Todavía no hay evaluaciones en el sistema.</TablaVacia>
            )}
            {lista.data.map((e) => (
              <Fila key={e.id}>
                <Td>
                  <div className="font-medium">{e.persona}</div>
                  <div className="text-xs" style={{ color: "var(--fg-muted)" }}>
                    {e.auto_enviada_en ? "Autoevaluación entregada" : "Sin autoevaluación"}
                  </div>
                </Td>
                <Td className="tabular-nums">
                  {comoFecha(e.desde)} – {comoFecha(e.hasta)}
                </Td>
                <Td>
                  <Insignia
                    tono={
                      e.estado === "CERRADA" ? "exito" : e.estado === "ANULADA" ? "neutro" : "info"
                    }
                  >
                    {ESTADO[e.estado]}
                  </Insignia>
                </Td>
                <Td className="text-right tabular-nums">
                  {nota(e.final)}
                  {e.nivel && <span className="block text-xs">{e.nivel}</span>}
                </Td>
                <Td>
                  <Link
                    to={`/talento-humano/evaluacion/${e.id}`}
                    className="foco-cristal text-sm underline"
                  >
                    Abrir
                  </Link>
                </Td>
              </Fila>
            ))}
          </Cuerpo>
        </Tabla>
      )}
    </section>
  )
}
